import React from 'react';
import styled from 'styled-components';
import { Button } from '../../ui/Button';

export interface HomepageHeroProps {
  /** Two-part headline. Defaults to the original locked couplet ("You know
   *  your people. / We find gifts they love.") — but note the live homepage
   *  no longer relies on that default: LandingPage passes explicit `headline`
   *  + `subhead` overrides (the working-mom copy, #294), so the default now
   *  only serves as the component fallback / stories. */
  headline?: [string, string];
  /** Which line of a CUSTOM headline gets the clay accent (0 or 1). Default:
   *  no accent. The default couplet keeps its baked-in line-1 accent. */
  accentLine?: 0 | 1;
  /** Collapse the two-sentence stanza gap to a hairline — for a custom
   *  headline that is ONE sentence split across two lines (the live homepage
   *  and /welcome2 both pass "Your gifting brain, / all in one place."). */
  tightHeadline?: boolean;
  /** Single supporting line under the headline. */
  subhead?: React.ReactNode;
  ctaLabel?: string;
  /**
   * Render the CTA yourself instead of the default clay `Button`, given the
   * resolved label and handler. For the gift-guides hub (THE-816), whose
   * Visual style table makes the page's primary button near-black rather than
   * clay — see `hub/HubFrameParts.tsx`.
   *
   * A RENDER PROP rather than a `React.ReactNode` slot so the label and the
   * click handler stay owned here: `ctaLabel`'s default and the caller's
   * `onCtaClick` are the hero's contract, and a plain node would let a caller
   * silently ship a CTA wired to nothing.
   */
  ctaSlot?: (label: string, onClick?: () => void) => React.ReactNode;
  onCtaClick?: () => void;
  /** Tighten the hero's bottom padding so the section below peeks above the
   *  fold — a scroll affordance (Kate 2026-07-21, /welcome2: the first
   *  FeatureStack card should peekaboo under the CTA). Default keeps the
   *  homepage's airy spacing. */
  compactBottom?: boolean;
  /**
   * THE-1081 (gift-guides hub preview, Airbnb-comp type cleanup): the
   * trimmed headline/subhead register — 56px/700 headline in the hub's own
   * ink (hsl(var(--foreground)), was 64px/800 in theme.color.inkSoft), 20px
   * regular gray subhead (was 24px/500 at 78% opacity). Opt-in so every
   * other caller (today's production homepage, /moms,
   * /teacher-appreciation) renders byte-identically.
   */
  compactHeadline?: boolean;
}

/* Default fallback copy (2026-06-03). This was the original locked
   first-time-homepage copy; since #294 the live `/` (and /welcome2) hero is
   the working-mom copy that LandingPage passes in via `headline`/`subhead`
   overrides, so this default is no longer rendered in production — it's the
   component's fallback (and what the unit test / stories exercise). Kept
   because the rationale still documents the voice: header carries the
   emotional you/we hook; subhead does the category education — "a place"
   (boards, not search), "everyone you love" (multi-recipient). Deliberately
   avoids "perfect gift" (singular → reinforces one-and-done) and
   "personalized" (reads as monogrammed). The default header breaks each
   sentence into two lines on mobile (Kate 2026-06-18); see HeadlineBreak. */
/* No background photo (Kate 2026-06-04: the image fought the text). Pure
   white field lets the headline carry the section. Dark ink type. */
const Section = styled.section`
  position: relative;
  width: 100%;
  background: #ffffff;
`;

const Inner = styled.div<{ $compactBottom?: boolean }>`
  max-width: 920px;
  margin: 0 auto;
  padding: 88px 16px ${({ $compactBottom }) => ($compactBottom ? '40px' : '72px')};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  @media (min-width: 768px) {
    padding: 132px 32px
      ${({ $compactBottom }) => ($compactBottom ? '48px' : '104px')};
  }
`;

const Headline = styled.h1<{ $compact?: boolean }>`
  font-family: ${({ theme }) => theme.font.sans};
  /* THE-1081 QA (compact only): the hero used to be the one place still on
     theme.color.inkSoft (#3D3530) while every other compact heading — the
     hub's own titles, TestimonialsCarousel's Heading — already renders
     hsl(var(--foreground)), which resolves to the identical #38322E. Two
     tokens for the same near-black was exactly the "two near-identical
     browns" the ticket called out; this collapses the compact page onto the
     one hub ink instead of carrying a second value forward. Default (non-
     compact) callers are unchanged. */
  color: ${({ theme, $compact }) => ($compact ? 'hsl(var(--foreground))' : theme.color.inkSoft)};
  font-weight: ${({ $compact }) => ($compact ? 700 : 800)};
  /* Bigger on mobile (Kate 2026-06-04). Scales with the viewport so each
     sentence stays unbroken and the couplet stays exactly two lines. */
  /* Mobile ~20% bigger (Kate 2026-07-23) — desktop sizes below (52/64px
     media queries) are unchanged, so this only scales < 768px. */
  font-size: ${({ $compact }) =>
    $compact ? 'clamp(32px, 9.2vw, 40px)' : 'clamp(35px, 10.8vw, 48px)'};
  line-height: 1.16;
  letter-spacing: -0.03em;
  margin: 0;
  @media (min-width: 768px) {
    font-size: ${({ $compact }) => ($compact ? '48px' : '52px')};
  }
  @media (min-width: 1024px) {
    font-size: ${({ $compact }) => ($compact ? '56px' : '64px')};
  }
`;

/* Second sentence on its own line so the you/we parallel reads as a
   couplet on every viewport. A clear stanza gap between the two sentences so
   the you→we call-and-response lands as two beats, not one block; the first
   sentence ("You know your people.") carries the clay accent for warmth, the
   second stays ink (Kate 2026-06-18). */
const HeadlineLine = styled.span<{
  $accent?: boolean;
  $gap?: 'stanza' | 'tight';
}>`
  display: block;
  color: ${({ $accent, theme }) => ($accent ? theme.color.clay : 'inherit')};
  /* Explicit per-line gap (set on the SECOND line only) instead of an
     "& + &" sibling selector: the two lines usually render with different
     dynamic classes ($accent), so the sibling form is fragile. 'stanza' is
     the two-sentence couplet rhythm; 'tight' is a hairline for a single
     sentence split across lines. */
  margin-top: ${({ $gap }) =>
    $gap === 'stanza' ? '0.55em' : $gap === 'tight' ? '0.1em' : '0'};
`;

/* On mobile each sentence breaks into two lines (Kate 2026-06-18):
   "You know / your people." — on desktop the clause flows back inline so
   each sentence is one line again. */
const HeadlineBreak = styled.span`
  display: block;
  @media (min-width: 768px) {
    display: inline;
  }
`;

const Subhead = styled.p<{ $compact?: boolean }>`
  font-family: ${({ theme }) => theme.font.sans};
  color: ${({ theme, $compact }) =>
    $compact ? theme.color.mutedText : theme.color.inkSoft};
  opacity: ${({ $compact }) => ($compact ? 1 : 0.78)};
  font-weight: ${({ $compact }) => ($compact ? 400 : 500)};
  /* Much larger on mobile (Kate 2026-06-18) — still clearly a subhead under
     the headline, just a bigger supporting line. */
  font-size: ${({ $compact }) => ($compact ? '18px' : '22px')};
  line-height: 1.4;
  letter-spacing: -0.01em;
  max-width: 30rem;
  margin: 18px 0 0;
  @media (min-width: 768px) {
    font-size: ${({ $compact }) => ($compact ? '20px' : '24px')};
    max-width: 38rem;
    margin-top: 22px;
  }
`;

/* Force the subhead's second clause to its own line on mobile so it breaks
   cleanly — "Discover and save gift ideas" / "for all your favorite people."
   On desktop it flows inline. (Kate 2026-06-04) */
const SubheadLine = styled.span`
  display: block;
  @media (min-width: 768px) {
    display: inline;
  }
`;

const CtaWrap = styled.div`
  display: flex;
  justify-content: center;
  /* Extra breathing room before the CTA on mobile (Kate 2026-06-04). */
  margin-top: 44px;
  @media (min-width: 768px) {
    margin-top: 40px;
  }
`;

export const HomepageHero: React.FC<HomepageHeroProps> = ({
  headline,
  accentLine,
  tightHeadline,
  subhead,
  ctaLabel = 'Get started',
  ctaSlot,
  onCtaClick,
  compactBottom,
  compactHeadline,
}) => {
  return (
    <Section aria-labelledby="homepage-hero-headline">
      <Inner $compactBottom={compactBottom}>
        <Headline id="homepage-hero-headline" $compact={compactHeadline}>
          {headline ? (
            <>
              <HeadlineLine $accent={accentLine === 0}>
                {headline[0]}
              </HeadlineLine>
              <HeadlineLine
                $accent={accentLine === 1}
                $gap={tightHeadline ? 'tight' : 'stanza'}
              >
                {headline[1]}
              </HeadlineLine>
            </>
          ) : (
            <>
              <HeadlineLine $accent>
                You know <HeadlineBreak>your people.</HeadlineBreak>
              </HeadlineLine>
              <HeadlineLine $gap="stanza">
                We find <HeadlineBreak>gifts they love.</HeadlineBreak>
              </HeadlineLine>
            </>
          )}
        </Headline>
        <Subhead $compact={compactHeadline}>
          {subhead ?? (
            <>
              Discover and save gift ideas{' '}
              <SubheadLine>for all your favorite people.</SubheadLine>
            </>
          )}
        </Subhead>
        <CtaWrap>
          {ctaSlot
            ? ctaSlot(ctaLabel, onCtaClick)
            : <Button label={ctaLabel} variant="primary" size="lg" onClick={onCtaClick} />}
        </CtaWrap>
      </Inner>
    </Section>
  );
};
